import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { SharedModule } from '@shared/shared.module';
import { EconomicGroupResponse } from '@interfaces/economic-groups/responses/economic-group.response';
import { FilterEconomicGroupRequest } from '@interfaces/economic-groups/requests/economic-group.request';
import { EconomicGroupsHttpService } from '@services/http/economic-groups/economic-groups-http.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { ConfirmationService } from '@src/app/core/services/helpers/confirmation.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { TranslateService } from '@ngx-translate/core';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { MenuItem } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { CreateEditGroupModalComponent } from '@src/app/features/dashboard/economic-groups/components/modals/create-edit-group-modal/create-edit-group-modal.component';

@Component({
  selector: 'app-economic-groups-list',
  standalone: true,
  imports: [CommonModule, SharedModule, CreateEditGroupModalComponent],
  templateUrl: './economic-groups-list.component.html',
  styleUrls: ['./economic-groups-list.component.scss'],
})
export class EconomicGroupsListComponent extends VaultBaseComponent implements OnInit, OnDestroy {
  @ViewChild('createEditModal') createEditModal?: CreateEditGroupModalComponent;

  private destroy$ = new Subject<void>();

  readonly loading = signal(false);
  readonly groups = signal<EconomicGroupResponse[]>([]);
  readonly total = signal(0);
  readonly canManage = signal(false);

  breadcrumbs: MenuItem[] = [];
  home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };
  columnDefinitions: ColumnDefinition[] = [
    new ColumnDefinition({ field: 'name', header: 'Nome', style: { width: '160px' } }),
    new ColumnDefinition({ field: 'memberCount', header: 'Empresas', style: { width: '100px' } }),
    // Setor padronizado do grupo (card 3642).
    new ColumnDefinition({
      field: 'companySectorName',
      header: 'Setor',
      style: { width: '180px' },
    }),
    new ColumnDefinition({ field: 'description', header: 'Descrição' }),
  ];

  private filter: FilterEconomicGroupRequest = { page: 1, pageSize: 20 };

  constructor(
    translateService: TranslateService,
    private readonly groupsService: EconomicGroupsHttpService,
    private readonly toast: ToastHelperService,
    private readonly confirmation: ConfirmationService,
    private readonly permissions: PermissionsService,
    private readonly router: Router
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    this.breadcrumbs = [{ label: 'Grupos Econômicos' }];

    this.permissions
      .hasPermission(Permissions.EconomicGroup.CREATE)
      .pipe(takeUntil(this.destroy$))
      .subscribe(has => this.canManage.set(has));

    this.loadGroups();
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    super.ngOnDestroy();
  }

  loadGroups(): void {
    this.loading.set(true);
    this.groupsService
      .getFiltered(this.filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          if (result) {
            this.groups.set(result.data);
            this.total.set(result.total);
          }
          this.loading.set(false);
        },
        error: () => {
          this.toast.showToastError('Erro ao carregar grupos econômicos.');
          this.loading.set(false);
        },
      });
  }

  openCreate(): void {
    this.createEditModal?.open();
  }

  openEdit(group: EconomicGroupResponse): void {
    this.createEditModal?.open(group);
  }

  openBuilder(group: EconomicGroupResponse): void {
    this.router.navigate(['/dashboard/economic-groups/builder', group.id]);
  }

  confirmDelete(group: EconomicGroupResponse): void {
    this.confirmation
      .confirm(
        'Excluir Grupo',
        `Deseja excluir o grupo "${group.name}"? Esta ação não pode ser desfeita.`
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(confirmed => {
        if (confirmed) this.deleteGroup(group.id);
      });
  }

  private deleteGroup(id: string): void {
    this.groupsService
      .delete(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.showToastSuccess('Grupo excluído com sucesso.');
          this.loadGroups();
        },
        error: () => this.toast.showToastError('Erro ao excluir grupo.'),
      });
  }

  onGroupSaved(): void {
    this.loadGroups();
  }

  getMenuActions(group: EconomicGroupResponse) {
    const actions: MenuItem[] = [
      {
        label: 'Abrir Builder',
        icon: 'pi pi-sitemap',
        command: () => this.openBuilder(group),
      },
    ];

    if (this.canManage()) {
      actions.push(
        { label: 'Editar', icon: 'pi pi-pencil', command: () => this.openEdit(group) },
        { label: 'Excluir', icon: 'pi pi-trash', command: () => this.confirmDelete(group) }
      );
    }

    return actions;
  }
}
